import {
  IconButton,
  ListItemIcon,
  makeStyles,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import ListAltIcon from '@material-ui/icons/ListAlt';
import MessageIcon from '@material-ui/icons/Message';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import PeopleIcon from '@material-ui/icons/People';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { useConfirm } from 'material-ui-confirm';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import { useSnackbar } from 'notistack';
import { useModal } from 'react-modal-hook';

import ModalDefinirEquipePadrao from 'components/ModalDefinirEquipePadrao';
import ModalIniciarContato from 'components/ModalIniciarContato';
import { useAppDispatch } from 'store';
import {
  ContatoInterfaceV2,
  removerContatoV2,
  removerEquipeOuAtendentePadrao,
} from 'store/reducers/contatos';
import { normalizeContato } from 'utils/normalizeContato';

import DrawerDetalhesContato from './DrawerDetalhesContato';

interface ContatoMenuAcoesProps {
  contato: ContatoInterfaceV2;
  setContato: React.Dispatch<
    React.SetStateAction<ContatoInterfaceV2 | undefined>
  >;
  onRefresh: () => void;
  onToggleSelection: () => void;
}

const useStyles = makeStyles(({ typography }) => ({
  paper: {
    '& .MuiDialogTitle-root .MuiTypography-h6': {
      fontSize: typography.pxToRem(14),
      fontWeight: typography.fontWeightMedium as number,
    },
    '& .MuiDialogContent-root .MuiDialogContentText-root': {
      fontSize: typography.pxToRem(14),
    },
  },
}));

export default function ContatoMenuAcoes({
  contato,
  setContato,
  onRefresh,
  onToggleSelection,
}: ContatoMenuAcoesProps) {
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const isBlocked = Boolean(contato?.blockedAt);
  const normalizedContato = normalizeContato(contato);

  const [showModalIniciarContato, hideModalIniciarContato] = useModal(
    ({ in: open }) => (
      <ModalIniciarContato
        open={open}
        contato={normalizedContato}
        onClose={() => {
          hideModalIniciarContato();
          setContato(undefined);
        }}
        onSuccess={() => setContato(undefined)}
      />
    ),
    [normalizedContato],
  );

  const [showModalDefinirEquipePadrao, hideModalDefinirEquipePadrao] = useModal(
    ({ in: open }) => (
      <ModalDefinirEquipePadrao
        open={open}
        contatoId={contato?.id}
        onClose={() => {
          hideModalDefinirEquipePadrao();
          setContato(undefined);
        }}
      />
    ),
    [contato?.id],
  );

  const [showDrawerDetalhesContato, hideDrawerDetalhesContato] = useModal(
    ({ in: open }) => {
      if (!contato) return null;

      return (
        <DrawerDetalhesContato
          open={open}
          onClose={hideDrawerDetalhesContato}
          contatoId={contato.id}
        />
      );
    },
    [contato],
  );

  const [showDrawerEditarContato, hideDrawerEditarContato] = useModal(
    ({ in: open }) => {
      if (!contato) return null;

      return (
        <DrawerDetalhesContato
          open={open}
          onClose={hideDrawerEditarContato}
          contatoId={contato.id}
          editMode
          onSuccess={() => {
            hideDrawerEditarContato();
          }}
        />
      );
    },
    [contato],
  );

  const handleDetalhesContato = (contato: ContatoInterfaceV2) => {
    setContato(contato);
    showDrawerDetalhesContato();
  };

  const handleEditarContato = (contato: ContatoInterfaceV2) => {
    setContato(contato);
    showDrawerEditarContato();
  };

  const handleIniciarContato = (contato: ContatoInterfaceV2) => {
    setContato(contato);
    showModalIniciarContato();
  };

  const handleDefinirEquipePadrao = (contato: ContatoInterfaceV2) => {
    setContato(contato);
    showModalDefinirEquipePadrao();
  };

  const handleRemoverEquipePadrao = (contato: ContatoInterfaceV2) => {
    confirm({
      title: 'Remover atendente padrão',
      description: `Deseja remover a equipe/atendente padrão atribuido ao contato ${contato.name}?`,
      cancellationText: 'Cancelar',
      confirmationText: 'Remover',
      confirmationButtonProps: { color: 'secondary' },
      dialogProps: { maxWidth: 'xs', classes: { paper: classes.paper } },
    })
      .then(() => {
        dispatch(removerEquipeOuAtendentePadrao(contato.id))
          .unwrap()
          .then(() => {
            onRefresh();
            enqueueSnackbar(
              `O contato ${contato.name} não possui mais uma equipe/atendente vinculado a ele.`,
            );
          })
          .catch(error => {
            if (error.response) enqueueSnackbar(error.response.data.message);
          });
      })
      .catch(() => undefined);
  };

  const handleConfirmRemove = (contato: ContatoInterfaceV2) => {
    if (contato) {
      confirm({
        title: <Typography variant="body2">Remover contato</Typography>,
        description: (
          <Typography variant="caption">
            {`Deseja mesmo remover ${contato.name} da sua lista de contatos?`}
          </Typography>
        ),
        confirmationText: 'Remover',
        cancellationText: 'Cancelar',
        confirmationButtonProps: {
          color: 'secondary',
          variant: 'contained',
          autoFocus: true,
        },
        dialogProps: { maxWidth: 'xs' },
      })
        .then(async () => {
          await dispatch(removerContatoV2(contato.id))
            .unwrap()
            .then(onRefresh)
            .catch(error => {
              if (error.response) enqueueSnackbar(error.response.data.message);
            });
        })
        .catch(() => {
          //
        });
    }
  };

  return (
    <PopupState variant="popover" popupId={`contato-menu-${contato.id}`}>
      {popupState => (
        <>
          <IconButton
            size="small"
            {...bindTrigger(popupState)}
            onClick={event => {
              onToggleSelection();
              bindTrigger(popupState).onClick(event);
            }}
          >
            <MoreHorizIcon />
          </IconButton>
          <Menu {...bindMenu(popupState)}>
            <MenuItem
              onClick={() => {
                handleDetalhesContato(contato);
                popupState.close();
              }}
            >
              <ListItemIcon style={{ minWidth: 28 }}>
                <ListAltIcon fontSize="small" />
              </ListItemIcon>

              <Typography variant="body2">Detalhes</Typography>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleEditarContato(contato);
                popupState.close();
              }}
            >
              <ListItemIcon style={{ minWidth: 28 }}>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2">Editar contato</Typography>
            </MenuItem>
            {contato.status !== 'INVALIDO' && (
              <Tooltip
                title={
                  isBlocked
                    ? 'Contato bloqueado. Não é possível iniciar atendimento.'
                    : ''
                }
                arrow
              >
                <span>
                  <MenuItem
                    onClick={() => {
                      handleIniciarContato(contato);
                      popupState.close();
                    }}
                    disabled={isBlocked}
                  >
                    <ListItemIcon style={{ minWidth: 28 }}>
                      <MessageIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="body2">Iniciar contato</Typography>
                  </MenuItem>
                </span>
              </Tooltip>
            )}
            {contato.status === 'VALIDO' && !contato.teamDefault && (
              <MenuItem
                onClick={() => {
                  handleDefinirEquipePadrao(contato);
                  popupState.close();
                }}
              >
                <ListItemIcon style={{ minWidth: 28 }}>
                  <PeopleIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2">Definir equipe padrão</Typography>
              </MenuItem>
            )}
            {contato.status === 'VALIDO' && contato.teamDefault && (
              <MenuItem
                onClick={() => {
                  handleRemoverEquipePadrao(contato);
                  popupState.close();
                }}
              >
                <ListItemIcon style={{ minWidth: 28 }}>
                  <RemoveCircleOutlineIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2">
                  Remover atendente padrão
                </Typography>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleConfirmRemove(contato);
                popupState.close();
              }}
            >
              <ListItemIcon style={{ minWidth: 28 }}>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <Typography color="error" variant="body2">
                Remover
              </Typography>
            </MenuItem>
          </Menu>
        </>
      )}
    </PopupState>
  );
}
